import { useState } from "react";
import TableTransactionWorkflow from "../TableTransactionWorkflow/TableTransactionWorkflow";
import DesktopVending from "./DesktopVending";
import { MobileVendingCard } from "./MobileVending";
import { ReactComponent as RecheckIcon } from "../../../assets/img/recheckIcon.svg";
import { ReactComponent as FileIcon } from "../../../assets/img/fileIcoon.svg";
import { ReactComponent as PayIcon } from "../../../assets/img/payIcon.svg";
import BottomSheet from "../../BottomSheet/BottomSheet";
import useIsMobile from "../../../hooks/useIsMobile";
import { PAYMENT_STATUS_VALUE, PaymentMethod } from "../../Utils/constants";
import { vendingData } from "../../../helper/data";
import ModalContainer from "../../Modals/CustomModal/Modal";
import { MobileTableView } from "../../Table/CustomTable/MobileTable";
import Pagination from "../../Pagination/Pagination";
import { getTransactionId, verifyAndGenerateToken } from "../../../services/selfService";
import { toast } from "react-toastify";
import { remitaPaymentService } from "../../../services/payment";
import { usePowerPurchaseStore } from "../../../store/buyPowerStore";
import { downloadInvoiceHandler, downloadReceiptHandler } from "../../../helper/downloadHandler";
import { ErrorPaymentCard } from "../../BuyPowerWorkFlow/PaymentStatusCard";
import SuccessStep from "../../BuyPowerWorkFlow/SuccessStep";

interface VendingTableProps {
  vendingDataHook: ReturnType<typeof import("../../../hooks/useVendingData").useVendingData>;
  showViewAll?: boolean;
}
const VendingTable: React.FC<VendingTableProps> = ({ vendingDataHook, showViewAll }) => {
  // const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<any>(null);
  const [downloadingInvoice, setDownloadingInvoice] = useState(false);
  const [downloadingReceipt, setDownloadingReceipt] = useState(false);
  const { setPaymentResult, triggerRefetch } = usePowerPurchaseStore();
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [open, setOpen] = useState(false);
  const { data, loading, error, totalCount, currentPage, pageSize, handlePageChange, refetch } = vendingDataHook;
  const isMobile = useIsMobile();

  const handleRecheck = async (selectedTransaction: any) => {
    try {
      const remitaResponseData = selectedTransaction.remitaResponse || "anything";
      // Verify payment on backend

      const verificationResponse = await verifyAndGenerateToken({
        transactionId: selectedTransaction.transactionId as string,
        amount: selectedTransaction.amount,
        meterNo: selectedTransaction.meterNumber,
        paymentMethod: PaymentMethod.Remita,
        invoiceNumber: selectedTransaction.invoiceNumber,
        remitaPaymentResponse: remitaResponseData,
      });
      if (verificationResponse.data.isSuccessful && verificationResponse.data.data) {
        toast.success("Recheck successful! Payment status updated.");
        refetch();
        hideAction();
      }
    } catch (error: any) {
      const errorMessage = error?.data?.message || error.message;
      toast.error(errorMessage || "An error occurred during recheck.");
    }
  };

  const hideAction = () => {
    setSelectedIconIndex(null);
    setShowTransactionModal(false);
    setSelectedTransaction(null);
  };

  const handlePayOnline = async (selectedTransaction: any) => {
    try {
      let payload = {
        transactionId: selectedTransaction.transactionId,
        amount: selectedTransaction.amount,
        meterNo: selectedTransaction.meterNumber,
        paymentMethod: PaymentMethod.Remita,
        invoiceNumber: selectedTransaction.invoiceNumber,
        remitaPaymentResponse: "",
      };

      // before initializing payment, generate transaction Id
      const transactionIdResponse = await getTransactionId({
        invoiceNumber: selectedTransaction.invoiceNumber,
        transactionId: selectedTransaction.transactionId as string,
      });

      if (!transactionIdResponse?.data?.isSuccessful || !transactionIdResponse?.data?.data) {
        toast.error("Failed to generate transaction ID. Please try again.");
        return;
      }

      const transactionId = transactionIdResponse.data.data;
      payload.transactionId = transactionId;
      await remitaPaymentService.initializePayment(
        {
          key: "", // From env
          rrr: selectedTransaction.rrr,
          transactionId: transactionId,
          amount: selectedTransaction.amount,
        },
        {
          onSuccess: async (remitaResponse) => {
            const remitaResponseData = remitaResponse ? JSON.stringify(remitaResponse) : "anything";
            // Verify payment on backend
            payload.remitaPaymentResponse = remitaResponseData;

            const verificationResponse = await verifyAndGenerateToken({
              ...payload,
            });

            if (verificationResponse.data.isSuccessful && verificationResponse.data.data) {
              const verificationResponseData = verificationResponse.data.data;
              setPaymentResult({
                token: verificationResponseData?.token,
                amount: verificationResponseData.amount,
                equivalentUnit: verificationResponseData.equivalentUnit,
                paymentDate: verificationResponseData.paymentDate,
                email: verificationResponseData.email,
                receiptNumber: verificationResponseData?.receiptNumber,
              });

              refetch();

              setShowSuccessModal(true);

              toast.success("Payment successful!");
            } else {
              toast.error("Payment verification failed. Please contact support.");
              setShowErrorModal(true);
            }
            triggerRefetch();
          },
          onError: async (remitaResponse) => {
            toast.error(remitaResponse.message || "Payment failed");
            // setIsProcessingPayment(false);
            const remitaResponseData = remitaResponse ? JSON.stringify(remitaResponse) : "anything";
            // Verify payment on backend
            payload.remitaPaymentResponse = remitaResponseData;
            await verifyAndGenerateToken({
              ...payload,
            });

            setShowErrorModal(true);
          },

          onClose: () => {
            // setIsProcessingPayment(false);
            // setLoading(false);
          },
        }
      );
    } catch (error: any) {
      const errorMessage = error.data?.message || error.message;
      toast.error(errorMessage || "Failed to initialize payment");
      // setIsProcessingPayment(false);
      // setLoading(false);
    }
  };

  const tableActions = [
    {
      title: "Recheck",
      allowOnly: PAYMENT_STATUS_VALUE.PENDING,
      action: () => {
        // hideAction();
        handleRecheck(selectedTransaction);
      },
      icon: <RecheckIcon />,
    },
    {
      title: "Download Invoice",
      allowOnly: PAYMENT_STATUS_VALUE.UNPAID,
      action: async () => {
        await downloadInvoiceHandler(selectedTransaction?.invoiceNumber, {
          onStart: () => setDownloadingInvoice(true),
          onFinally: () => {
            setDownloadingInvoice(false);
            hideAction();
          },
        });
      },
      icon: <FileIcon />,
    },
    {
      title: "Make Payment",
      allowOnly: PAYMENT_STATUS_VALUE.UNPAID,
      action: () => {
        hideAction();
        handlePayOnline(selectedTransaction);
      },
      icon: <PayIcon />,
    },
    {
      title: "Download Reciept",
      allowOnly: PAYMENT_STATUS_VALUE.SUCCESS,
      action: async () => {
        if (!selectedTransaction.invoiceNumber && !selectedTransaction.receiptNumber) {
          toast.error("Invoice or receipt number not found");
          return;
        }
        await downloadReceiptHandler(selectedTransaction.invoiceNumber, selectedTransaction.receiptNumber, {
          onStart: () => setDownloadingReceipt(true),
          onFinally: () => {
            setDownloadingReceipt(false);
            hideAction();
          },
        });
      },
      icon: <FileIcon />,
    },

    {
      title: "Download Reciept",
      allowOnly: PAYMENT_STATUS_VALUE.PAID,

      action: async () => {
        if (!selectedTransaction.invoiceNumber && !selectedTransaction.receiptNumber) {
          toast.error("Invoice or receipt number not found");
          return;
        }
        await downloadReceiptHandler(selectedTransaction.invoiceNumber, selectedTransaction.receiptNumber, {
          onStart: () => setDownloadingReceipt(true),
          onFinally: () => {
            setDownloadingReceipt(false);
            hideAction();
          },
        });
      },
      icon: <FileIcon />,
    },
  ];

  const [selectedIconIndex, setSelectedIconIndex] = useState<number | null>(null);

  const handleTableActionClick = (item: any) => {
    console.log(item);
    // setSelectedIconIndex(item);
    setSelectedTransaction(item);
    setShowTransactionModal(true);
    // setOpen(true);
  };

  // Loading state
  if (loading && currentPage === 1) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading transactions...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button onClick={() => refetch()} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Empty state
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-gray-600 mb-2">No transactions found</p>
        </div>
      </div>
    );
  }
  const mobileView = (
    <>
      <MobileTableView
        data={data || []}
        renderCard={(item: any) => (
          <MobileVendingCard key={item.id} data={item} onClick={() => handleTableActionClick(item)} />
        )}
        emptyMessage="No vending transactions found"
      />
      {totalCount > pageSize && (
        <div className="w-full my-4">
          <Pagination
            totalItems={totalCount}
            itemsPerPage={pageSize || 10}
            onPageChange={handlePageChange}
            currentPage={currentPage}
          />
        </div>
      )}
    </>
  );

  return (
    <div>
      {/* Mobile View */}
      <div className={`block md:hidden`}>{mobileView}</div>

      {/* Desktop View */}
      <div className={`hidden md:block`}>
        <DesktopVending
          selectedIconIndex={selectedIconIndex}
          currentPageNumber={currentPage}
          data={data || vendingData}
          open={open}
          loading={loading}
          totalCount={totalCount}
          pageSize={pageSize}
          setOpen={setOpen}
          setSelectedTransaction={setSelectedTransaction}
          setShowTransactionModal={setShowTransactionModal}
          handleTableActionClick={handleTableActionClick}
          handlePageChange={handlePageChange}
          showViewAll={showViewAll}
        />
      </div>

      {!isMobile && (
        <>
          <ModalContainer width={600} showModal={showTransactionModal} setShowModal={setShowTransactionModal}>
            <TableTransactionWorkflow
              selectedTransaction={selectedTransaction}
              tableActions={tableActions}
              hideAction={() => {
                setShowTransactionModal(false);
                setSelectedTransaction(null);
              }}
              type="vending"
            />
          </ModalContainer>
          <ModalContainer
            showModal={showSuccessModal}
            showGradient
            variant="success"
            setShowModal={setShowSuccessModal}
            width={600}
          >
            <SuccessStep title="Transaction Successful" onClose={() => setShowSuccessModal(false)} />
          </ModalContainer>

          {/* Error Modal - separate */}
          <ModalContainer
            showModal={showErrorModal}
            showGradient
            variant="error"
            setShowModal={setShowErrorModal}
            width={600}
          >
            <ErrorPaymentCard
              title="Error processing payment"
              message="We couldn't process your payment."
              primaryButtonText="Try again"
              secondaryButtonText="Close"
              onPrimaryClick={() => console.log("retry")}
              onSecondaryClick={() => console.log("close")}
              onClose={() => console.log("close")}
            />
          </ModalContainer>
        </>
      )}
      {isMobile && (
        <>
          <BottomSheet
            openBottomSheet={showTransactionModal}
            setOpenBottomSheet={setShowTransactionModal}
            onCloseAction={() => setShowTransactionModal(false)}
            showGradient
            variant="success"
          >
            <TableTransactionWorkflow
              selectedTransaction={selectedTransaction}
              tableActions={tableActions}
              hideAction={() => {
                setOpen(false);
                setSelectedTransaction(null);
              }}
              type="vending"
            />
          </BottomSheet>
          <BottomSheet
            title={""}
            openBottomSheet={showSuccessModal}
            setOpenBottomSheet={setShowSuccessModal}
            onCloseAction={() => {
              setShowSuccessModal(false);
            }}
            showGradient
            variant="success"
          >
            <SuccessStep title="Transaction Successful" onClose={() => setShowSuccessModal(false)} />
          </BottomSheet>

          {/* // Error Modal - separate */}
          <BottomSheet
            title={""}
            openBottomSheet={showErrorModal}
            setOpenBottomSheet={setShowErrorModal}
            onCloseAction={() => {
              setShowErrorModal(false);
            }}
            showGradient
            variant="error"
          >
            <ErrorPaymentCard
              title="Error processing payment"
              message="We couldn't process your payment."
              primaryButtonText="Try again"
              secondaryButtonText="Close"
              onPrimaryClick={() => console.log("retry")}
              onSecondaryClick={() => console.log("close")}
              onClose={() => console.log("close")}
            />
          </BottomSheet>
        </>
      )}
    </div>
  );
};

export default VendingTable;
